import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import Button from "app/storybookComponents/Button";
import { Card, OverlayTrigger, Popover } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChatInfoBanner } from "app/components/Chat";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import { cn } from "app/lib/utils";
import {
  selectChatbotMessages,
  selectThreadId,
  selectSendMessageStatus,
  resetChatbotState,
  selectSelectedChatbotPrompt,
  setCoachBotPrompt,
  sendGeneralMessage,
  sendGeneralLoginlessMessage,
  selectGenerateInstructionsStatus,
  selectDirectReportAuthError,
  regenerateLastMessage,
  selectSubmittingCoachbotFeedbackStatus,
  submitCoachbotFeedback,
  setIsCoachbotOpen,
  selectChatbotFullScreen,
  setChatbotFullScreen,
  selectIsThreadIdWeeklyCheckinMap,
  selectSampleChatState,
  selectGetCoachBoConversationsStatus,
  selectGetIntroMessageStatus,
  selectStreamingResponse,
  selectIsStreaming,
  selectActiveToolCall,
  selectCompletedToolCalls,
} from "./slice";
import { selectLoginlessMessageLimitReached } from "app/containers/LoginlessWeeklyCheckinView/slice";
import { getAuthenticatedAppUrl } from "app/containers/LoginlessWeeklyCheckinView/helpers";
import ChatInput from "app/components/Chat/ChatInput";
import LoadingSpinner from "app/components/Chat/LoadingSpinner";
import ChatAvatar from "app/components/Chat/ChatAvatar";
import { ScrollArea } from "app/shadcnComponents/ui/scroll-area";
import Prompt from "./Prompt";
import MessageBubble from "./MessageBubble";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CHATBOT_FEEDBACK_EXAMPLES,
  CHATBOT_OTHER_FEEDBACK,
  CoachbotDefaultPrompts,
} from "./constants";
import { CoachbotCardTemplate } from "../CoachBotPrompts/types";
import {
  ONE_WEEK_SMART_GOAL_TEMPLATE,
  REFLECT_OR_DEBRIEF_TEMPLATE,
} from "../CoachBotPrompts/constants";
import TextArea from "app/storybookComponents/Forms/TextArea";
import { ToolCallPending, ToolCallCompleted } from "./ToolCallAccordion";
import { selectIsHierarchyReadOnly } from "app/containers/Hierarchy/slice";

interface Props {
  readonly closeChatbot?: () => void;
  readonly isLoginless?: boolean;
}

export default function ChatbotContent({
  closeChatbot,
  isLoginless = false,
}: Props) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { encryptedThreadId } = useParams<{ encryptedThreadId: string }>();
  // ---------------------------------------- Selectors ----------------------------------------
  const messages = useAppSelector(selectChatbotMessages);
  const threadId = useAppSelector(selectThreadId);
  const sendMessageStatus = useAppSelector(selectSendMessageStatus);
  const isHierarchyReadOnly = useAppSelector(selectIsHierarchyReadOnly);
  const selectedCoachbotPrompt = useAppSelector(selectSelectedChatbotPrompt);
  const generateInstructionsStatus = useAppSelector(
    selectGenerateInstructionsStatus
  );
  const getIntroMessageStatus = useAppSelector(selectGetIntroMessageStatus);
  const directReportAuthError = useAppSelector(selectDirectReportAuthError);
  const submittingFeedbackStatus = useAppSelector(
    selectSubmittingCoachbotFeedbackStatus
  );
  const isFullScreen = useAppSelector(selectChatbotFullScreen);
  const isThreadIdWeeklyCheckinMap = useAppSelector(
    selectIsThreadIdWeeklyCheckinMap
  );
  const sampleChatState = useAppSelector(selectSampleChatState);
  const getCoachBoConversationsStatus = useAppSelector(
    selectGetCoachBoConversationsStatus
  );
  const loginlessMessageLimitReached = useAppSelector(
    selectLoginlessMessageLimitReached
  );
  const streamingResponse = useAppSelector(selectStreamingResponse);
  const isStreaming = useAppSelector(selectIsStreaming);
  const activeToolCall = useAppSelector(selectActiveToolCall);
  const completedToolCalls = useAppSelector(selectCompletedToolCalls);

  // ---------------------------------------- State ----------------------------------------
  const [message, setMessage] = useState("");
  const [isHelpful, setIsHelpful] = useState<null | boolean>(null);
  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [feedbackInputText, setFeedbackInputText] = useState("");
  const [showThanksForFeedbackMessage, setShowThanksForFeedbackMessage] =
    useState(false);

  // ---------------------------------------- Refs ----------------------------------------
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const loadingMessageRef = useRef<HTMLDivElement | null>(null);

  // Scroll to bottom when messages change or when chat loads
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle scroll and focus when sending messages
  useEffect(() => {
    if (sendMessageStatus === "succeeded") {
      inputRef.current?.focus();
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (sendMessageStatus === "loading") {
      loadingMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [sendMessageStatus]);

  // Auto-scroll when streaming response updates
  useEffect(() => {
    if (isStreaming && streamingResponse) {
      loadingMessageRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [isStreaming, streamingResponse]);

  const getLoadingMessage = useCallback((): string => {
    const options = [
      "Please wait, Coach Bo is on the way...",
      "Please wait, Coach Bo is reviewing your situation...",
      "Please wait, Coach Bo is running down the hall...",
      "Please wait, Coach Bo is finding some files...",
      "Please wait, Coach Bo is just finishing a call...",
    ];
    const randomIndex = Math.floor(Math.random() * options.length);
    return options[randomIndex];
  }, []);

  const setIsFullScreen = (value: boolean) => {
    dispatch(setChatbotFullScreen(value));
  };

  const closeButtonWrapper = () => {
    closeChatbot?.();
    setIsFullScreen(false);
  };

  // Computed values for cleaner conditional logic
  const isWeeklyCheckinChat = useMemo(
    () =>
      isLoginless ||
      !!(threadId && isThreadIdWeeklyCheckinMap[threadId]) ||
      (sampleChatState.isSampleChatShowing &&
        sampleChatState.isCheckingSampleChat),
    [isLoginless, threadId, isThreadIdWeeklyCheckinMap, sampleChatState]
  );

  // Dynamic class names for container styling
  const containerClasses = useMemo(
    () => ({
      chatContainer: isFullScreen
        ? "flex-col max-w-[768px]"
        : "flex-col-reverse max-w-full",
      inputContainer: isLoginless ? "max-w-[768px]" : "",
    }),
    [isFullScreen, isLoginless]
  );

  const reversedMessages = messages ? [...messages].reverse() : [];

  const showSuggestedPrompts = !message && messages.length <= 1;

  // Helper function to count user messages
  const countUserMessages = (messages: any[]): number => {
    return messages.filter((msg) => msg.role === "user").length;
  };

  const handleSendMessage = (messageToSend: string) => {
    if (!messageToSend) return;
    if (isHierarchyReadOnly) return;

    // Check message limit for loginless mode
    if (isLoginless) {
      const currentUserMessageCount = countUserMessages(messages);
      if (currentUserMessageCount >= 10 || loginlessMessageLimitReached) {
        // Show error notification - this will be handled by the toast in the Redux thunk
        return;
      }
    }

    // Simplified message sending logic
    if (isLoginless && encryptedThreadId) {
      dispatch(
        sendGeneralLoginlessMessage({
          message: messageToSend,
          encryptedThreadId,
        })
      );
    } else {
      dispatch(sendGeneralMessage({ message: messageToSend }));
    }

    setMessage("");
    setIsHelpful(null);
  };

  const getSuggestedPromptsArray = (): string[] => {
    if (selectedCoachbotPrompt?.userPrompts) {
      return selectedCoachbotPrompt?.userPrompts;
    }
    const promptMapCopy: {
      [key: string]: string;
    } = { ...CoachbotDefaultPrompts };

    if (pathname === "/CoachBoPrompts") {
      delete promptMapCopy.SeeAllPromptTitle;
    }
    return Object.values(promptMapCopy);
  };

  const onTemplateSelect = async (
    template: CoachbotCardTemplate,
    message: string
  ) => {
    dispatch(resetChatbotState());
    dispatch(setIsCoachbotOpen(true));
    await dispatch(
      setCoachBotPrompt({
        userPrompts: template.preSelectedPrompts ?? [],
        coachBotIntro: "",
        chatType: template.chatType ?? "",
      })
    );
    handleSendMessage(message);
  };

  const handlePromptSelect = (prompt: string) => {
    switch (prompt) {
      case CoachbotDefaultPrompts.SeeAllPromptTitle:
        navigate("/CoachBoPrompts");
        closeButtonWrapper();
        break;
      case CoachbotDefaultPrompts.CreateOneWeekGoal:
        onTemplateSelect(ONE_WEEK_SMART_GOAL_TEMPLATE, prompt);
        break;
      case CoachbotDefaultPrompts.ReflectOnOverall:
        onTemplateSelect(REFLECT_OR_DEBRIEF_TEMPLATE, prompt);
        break;
      default:
        handleSendMessage(prompt);
    }
  };

  const getSuggestedPrompts = () => {
    const suggestedPrompts = [...getSuggestedPromptsArray()];
    if (
      isHierarchyReadOnly ||
      generateInstructionsStatus === "loading" ||
      getIntroMessageStatus === "loading" ||
      sampleChatState.isCheckingSampleChat ||
      suggestedPrompts.length === 0
    ) {
      return null;
    }

    return (
      <div className="border-t border-r border-l border-[#d9def4] rounded mt-5 mx-2.5 mb-2.5">
        {suggestedPrompts.map((prompt) => (
          <Prompt
            key={prompt}
            prompt={prompt}
            handleSendMessage={handlePromptSelect}
          />
        ))}
      </div>
    );
  };

  const getButton = (
    fontIcon: "arrows-rotate" | "thumbs-up" | "thumbs-down",
    popoverText: string,
    onClick: () => void
  ) => {
    const overlay = (
      <Popover className="team-360-popover">
        <div>{popoverText}</div>
      </Popover>
    );

    return (
      <OverlayTrigger trigger={["hover", "focus"]} overlay={overlay}>
        <div>
          <Button
            variant="secondary-blue"
            onClick={onClick}
            style={{
              padding: "6px 8px",
              border: 0,
            }}
          >
            <FontAwesomeIcon icon={fontIcon} />
          </Button>
        </div>
      </OverlayTrigger>
    );
  };

  const onSubmitFeedback = async (feedback: string) => {
    // send request to the api
    if (isHelpful === null) {
      return;
    }
    await dispatch(
      submitCoachbotFeedback({
        feedback,
        feedbackType: isHelpful ? "thumbsUp" : "thumbsDown",
      })
    );
    setShowThanksForFeedbackMessage(true);
    setTimeout(() => {
      setShowThanksForFeedbackMessage(false);
      setIsHelpful(null);
    }, 4000);
  };

  const getTellUsMore = () => {
    if (isHelpful === null) {
      return null;
    }

    if (showThanksForFeedbackMessage) {
      return (
        <div
          className={cn(
            "transition-opacity duration-500",
            showThanksForFeedbackMessage ? "opacity-100" : "opacity-0"
          )}
        >
          Thanks for the feedback!
        </div>
      );
    }

    const xButton = (
      <Button
        xIcon
        style={{
          position: "absolute",
          right: "10px",
          top: "10px",
          padding: "1px 4px",
          border: 0,
        }}
        variant="secondary-blue"
        onClick={() => {
          setIsHelpful(null);
          setShowFeedbackInput(false);
        }}
      />
    );
    if (showFeedbackInput) {
      return (
        <Card className="p-2 position-relative">
          {xButton}
          <TextArea
            inputLabel="Let us know your comments:"
            inputText={feedbackInputText}
            onTextChange={(e) => setFeedbackInputText(e)}
            placeholder="Feel free to add any specific details here..."
            controlId="feedback-input"
          />
          <Button
            onClick={() => {
              onSubmitFeedback(feedbackInputText);
            }}
            disabled={!feedbackInputText}
            className="mt-3"
          >
            Submit
          </Button>
        </Card>
      );
    }

    const helpfulOrNot = isHelpful ? "helpful" : "notHelpful";
    return (
      <Card className="p-2 position-relative">
        {xButton}
        <p className="textarea-label mb-2">Tell us more:</p>
        <div className="flex flex-wrap gap-2">
          {CHATBOT_FEEDBACK_EXAMPLES[helpfulOrNot].map((tip) => (
            <Button
              onClick={() => {
                if (tip === CHATBOT_OTHER_FEEDBACK) {
                  setShowFeedbackInput(true);
                } else {
                  onSubmitFeedback(tip);
                }
              }}
              variant="secondary-blue"
              disabled={submittingFeedbackStatus === "loading"}
              key={tip}
            >
              {tip}
            </Button>
          ))}
        </div>
      </Card>
    );
  };

  const getFeedbackContainer = () => {
    if (
      isLoginless ||
      reversedMessages.length <= 1 ||
      reversedMessages[0]?.role !== "assistant"
    ) {
      return null;
    }

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex gap-1.5">
          {getButton("arrows-rotate", "Regenerate", () => {
            dispatch(regenerateLastMessage());
            setIsHelpful(null);
          })}
          {getButton("thumbs-up", "Good Response", () => {
            setIsHelpful(true);
          })}
          {getButton("thumbs-down", "Bad Response", () => {
            setIsHelpful(false);
          })}
        </div>
        {getTellUsMore()}
      </div>
    );
  };

  const getMessageContainer = () => {
    if (
      generateInstructionsStatus === "loading" ||
      getIntroMessageStatus === "loading" ||
      getCoachBoConversationsStatus === "loading"
    ) {
      return (
        <div className="flex flex-col items-center">
          <LoadingSpinner
            message={getLoadingMessage()}
            subMessage="This will take less than a minute."
          />
        </div>
      );
    }

    return (
      <ScrollArea className="flex-1 min-h-0 w-full">
        <div className="flex flex-col-reverse gap-3 max-w-[750px] mx-auto w-full">
          {getErrorBanner()}
          {/* Show streaming response or typing dots while loading */}
          {sendMessageStatus === "loading" && (
            <div ref={loadingMessageRef}>
              {/* Completed tool call accordions — bare, no bubble */}
              {completedToolCalls.length > 0 && (
                <div className="flex flex-col gap-0.5 pl-[54px] mb-1">
                  {completedToolCalls.map((tc, i) => (
                    <ToolCallCompleted
                      key={i}
                      label={tc.label}
                      success={tc.success}
                    />
                  ))}
                </div>
              )}
              {/* Active tool call (pending) or streaming response or typing dots */}
              {isStreaming && streamingResponse ? (
                <MessageBubble
                  type="assistant"
                  message={streamingResponse}
                  isPrivate={!isWeeklyCheckinChat}
                />
              ) : activeToolCall ? (
                <div className="flex items-end">
                  <ChatAvatar isPrivate={!isWeeklyCheckinChat} />
                  <ToolCallPending label={activeToolCall} />
                </div>
              ) : (
                <div className="flex items-end">
                  <ChatAvatar isPrivate={!isWeeklyCheckinChat} />
                  <div className="typing-dots py-1">
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={messagesEndRef} className="h-0" />
          {getFeedbackContainer()}
          {reversedMessages.map((message, index) => (
            <div key={index}>
              {message.role === "assistant" &&
                message.toolCalls &&
                message.toolCalls.length > 0 && (
                  <div className="flex flex-col gap-0.5 pl-[54px] mb-1">
                    {message.toolCalls.map((tc, i) => (
                      <ToolCallCompleted
                        key={i}
                        label={tc.label}
                        success={tc.success}
                      />
                    ))}
                  </div>
                )}
              <MessageBubble
                type={message.role}
                message={message.content}
                isPrivate={!isWeeklyCheckinChat}
              />
            </div>
          ))}
        </div>
      </ScrollArea>
    );
  };

  const getErrorBanner = () => {
    if (directReportAuthError) {
      return (
        <div className="warning-banner red d-flex align-items-center justify-content-between p-3 mb-2.5">
          <FontAwesomeIcon icon="exclamation-triangle" />
          <p>
            You don't have permission to view this direct report's information.
          </p>
        </div>
      );
    }
    if (sendMessageStatus === "failed" && !threadId) {
      return (
        <div className="warning-banner red d-flex align-items-center justify-content-between p-3 mb-2.5">
          <FontAwesomeIcon icon="exclamation-triangle" />
          <p>Coach Bo is currently unavailable. Please try again later.</p>
        </div>
      );
    }
    return null;
  };

  const getInfoBanner = () => {
    return (
      <ChatInfoBanner
        type={isWeeklyCheckinChat ? "weeklyCheckin" : "private"}
      />
    );
  };

  const getMessageLimitWarning = () => {
    // Check if message limit is reached for loginless mode
    const isMessageLimitReached =
      isLoginless &&
      (loginlessMessageLimitReached || countUserMessages(messages) >= 10);

    if (!isMessageLimitReached) {
      return null;
    }

    return (
      <div className="bg-[#FFEDCC] border border-[#FFEDCC] rounded p-3 mt-4 mb-[5px] flex items-center gap-2">
        <FontAwesomeIcon
          icon="exclamation-triangle"
          className="text-[#664100] text-base"
        />
        <div className="flex-1">
          <span className="text-[#664100] text-sm font-medium">
            You can only have 10 conversation turns without logging in. Please
            log into Develop to continue this conversation, if needed.
          </span>
        </div>
        <Button
          variant="link"
          className="text-[#664100] text-sm font-semibold p-0 border-none bg-transparent"
          onClick={() => {
            // Deep link to the conversation in the authenticated app
            const url = getAuthenticatedAppUrl(threadId);
            globalThis.location.href = url;
          }}
        >
          Go to Develop
        </Button>
      </div>
    );
  };

  const renderInputComponent = () => {
    // Check if message limit is reached for loginless mode
    const isMessageLimitReached =
      isLoginless &&
      (loginlessMessageLimitReached || countUserMessages(messages) >= 10);

    return (
      <ChatInput
        message={message}
        setMessage={setMessage}
        handleSendMessage={handleSendMessage}
        disabled={isHierarchyReadOnly || sendMessageStatus === "loading" || isMessageLimitReached}
        isLoginless={isLoginless}
        ref={inputRef}
      />
    );
  };

  return (
    <>
      {getInfoBanner()}
      {/* Render messages */}
      <div
        className={cn(
          "chatbot-messages-container flex flex-1 min-h-0 p-2.5 justify-start",
          isFullScreen && "mx-auto",
          containerClasses.chatContainer
        )}
      >
        {getMessageContainer()}
      </div>
      {!isLoginless && showSuggestedPrompts && getSuggestedPrompts()}
      <div
        className={cn(
          "w-full",
          isLoginless && "mx-auto",
          // Add 10px left/right/bottom margin only in non-fullscreen mode
          !isFullScreen && "px-2 pb-2",
          containerClasses.inputContainer
        )}
      >
        {renderInputComponent()}
        {/* Show message limit warning for loginless mode */}
        <div
          className={cn(
            "w-full",
            isLoginless && "mx-auto",
            containerClasses.inputContainer
          )}
        >
          {getMessageLimitWarning()}
        </div>
      </div>
    </>
  );
}
